import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { adminApi, messageFrom } from "@/lib/api";

/**
 * Everything the email panel needs to talk to the backend: the status payload,
 * the two previews, and which button is currently mid-flight.
 *
 * Every action follows the same shape — flag itself busy, do the call, hand a
 * 401 back to the dashboard, clear the flag — so `run` owns that shape once.
 */
export function useEmailPanel(onAuthError) {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(null);
  const [preview, setPreview] = useState(null);
  const [weekly, setWeekly] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setStatus(await adminApi.emailStatus());
    } catch (error) {
      if (!onAuthError?.(error)) toast.error(messageFrom(error));
    } finally {
      setLoading(false);
    }
  }, [onAuthError]);

  useEffect(() => {
    load();
  }, [load]);

  const run = useCallback(
    async (job, work) => {
      setBusy(job);
      try {
        await work();
      } catch (error) {
        if (!onAuthError?.(error)) toast.error(messageFrom(error));
      } finally {
        setBusy(null);
      }
    },
    [onAuthError],
  );

  const openPreview = useCallback(
    () => run("preview", async () => setPreview(await adminApi.digestPreview())),
    [run],
  );

  const openWeekly = useCallback(
    () => run("weekly-preview", async () => setWeekly(await adminApi.weeklyPreview())),
    [run],
  );

  const sendDigest = useCallback(
    () =>
      run("digest", async () => {
        const data = await adminApi.sendDigest();
        toast.success(
          `Chase list sent to ${data.to.join(", ")} — ${data.total} lead${
            data.total === 1 ? "" : "s"
          } to chase.`,
        );
        load();
      }),
    [run, load],
  );

  const sendWeekly = useCallback(
    () =>
      run("weekly-send", async () => {
        const data = await adminApi.sendWeekly();
        toast.success(
          `Week in review sent to ${data.to.join(", ")} — ${data.quoted} quote${
            data.quoted === 1 ? "" : "s"
          } this week.`,
        );
        load();
      }),
    [run, load],
  );

  const sendTest = useCallback(
    () =>
      run("test", async () => {
        const data = await adminApi.sendTestEmail();
        toast.success(`Test email sent to ${data.delivery.to}.`);
        load();
      }),
    [run, load],
  );

  /** Ask Resend again whether our own domain may send. Nothing reaches an inbox. */
  const checkDomain = useCallback(
    () =>
      run("domain-check", async () => {
        const data = await adminApi.checkEmailDomain();
        if (data.ok) {
          toast.success("Resend now accepts your domain — customers can be emailed.");
        } else {
          toast.error(data.check?.detail || "Resend still won't send from your domain.");
        }
        load();
      }),
    [run, load],
  );

  return {
    loading,
    status,
    busy,
    preview,
    weekly,
    setPreview,
    setWeekly,
    actions: { openPreview, openWeekly, sendDigest, sendWeekly, sendTest, checkDomain },
  };
}
